import changeCase from 'change-case'
import { array, bool, object, string } from 'prop-types'
import { Fragment } from 'react'
import { AnalyticsLayer } from '@opallabs/quanta'
import { kuiperClassNames, isFunction } from '../../utils'
import Cell from '../Cell'
import ComponentCell from '../ComponentCell'
import ContentCell from '../ContentCell'
import CurrencyCell from '../CurrencyCell'
import DatePickerCell from '../DatePickerCell'
import columnTypes from '../helpers/columnTypes'
import SearchableListCell from '../SearchableListCell'
import TextCell from '../TextCell'
import './styles.scss'

const getCellComponent = (
  columnType,
  availableForEntity,
  columnIndex,
  rowType,
  entityType
) => {
  const isFirstColumn = columnIndex === 0
  const rowIsEmpty = rowType === 'empty'
  const rowIsEditable = isFunction(availableForEntity)
    ? Boolean(availableForEntity({ entityType }))
    : rowType !== 'group'

  if (rowIsEmpty) return Cell
  if (!rowIsEditable) return isFirstColumn ? TextCell : Cell
  if (isFirstColumn) return ContentCell

  switch (columnType) {
    case columnTypes.text:
      return TextCell
    case columnTypes.content:
      return ContentCell
    case columnTypes.searchableList:
      return SearchableListCell
    case columnTypes.currency:
      return CurrencyCell
    case columnTypes.component:
      return ComponentCell
    case columnTypes.datePicker:
      return DatePickerCell
    default:
      return TextCell
  }
}

const renderCells = (props, columns) =>
  columns.map((column, columnIndex) => {
    if (!column.visible) return null

    const CellToRender = getCellComponent(
      column.type,
      column.availableForEntity,
      columnIndex,
      props.rowType,
      props.entityType
    )

    const cellColumnActive = props.activeCellIndex === columnIndex
    const cellRowActive = props.activeRowIndex === props.rowIndex
    const currentCellFocused = cellColumnActive && cellRowActive
    const isTabbable = isFunction(column.tabbable)
      ? Boolean(column.tabbable(props) && props.entityId)
      : column.tabbable

    const onSelect = (...args) => {
      column.onSelect(...args)
      if (props.focusCell) props.focusCell()
    }

    return (
      <Fragment key={`${props.entityId}-${columnIndex}`}>
        <AnalyticsLayer name={`${column.title} Column`}>
          <CellToRender
            {...props}
            accountId={parseInt(props.accountId, 10)}
            activateCell={props.activateCell}
            active={props.cellIsActive}
            approvalStatus={props.approvalStatus}
            cellIndex={columnIndex}
            className={kuiperClassNames(
              'cell',
              changeCase.kebab(column.type),
              column.className
            )}
            editable={column.editable}
            focused={currentCellFocused}
            getCellContents={column.getCellContents}
            getCellOptions={column.getCellOptions}
            getLeftSlot={column.getLeftSlot}
            getMatchingId={column.getMatchingId}
            getMaxLength={column.getMaxLength}
            getRightSlot={column.getRightSlot}
            onCellBlur={props.handleCellBlur}
            isRemovable={column.isRemovable}
            key={`${props.entityId}-${columnIndex}`}
            onInputBlur={column.onBlur}
            onChange={column.onChange}
            onClick={column.onClick}
            onSelect={onSelect}
            startOfWeek={column.startOfWeek}
            tabbable={isTabbable}
            title={column.title}
            width={props.columnWidths && props.columnWidths[columnIndex]}
          />
        </AnalyticsLayer>
      </Fragment>
    )
  })

const Row = (props) => {
  const {
    entityType,
    entityIsFirst,
    entityIsLast,
    rowType,
    columns,
    style,
    multiRow,
  } = props

  const cells = renderCells(props, columns)
  return (
    <tr
      className={kuiperClassNames('row', {
        [entityType]: entityType,
        [rowType]: rowType,
        first: entityIsFirst,
        last: entityIsLast,
        'multi-row': multiRow,
      })}
      style={style}
    >
      {cells}
    </tr>
  )
}

Row.propTypes = {
  columns: array.isRequired,
  entityIsFirst: bool,
  entityIsLast: bool,
  entityType: string,
  multiRow: bool,
  rowType: string,
  style: object,
}

export default Row
